import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { User, Plus, Search, ChevronDown, ChevronUp } from 'lucide-react';
import AthleteCard from './AthleteCard';

const INITIAL_DISPLAY_COUNT = 12;

export default function AthletesGrid({ 
  athletes, 
  childrenData, 
  medicalStatuses,
  hasActiveFilters, 
  onClearFilters,
  onDelete,
  onRefresh,
  showAllByDefault = false
}) {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(showAllByDefault);

  const handleDelete = (childId) => {
    if (onDelete) {
      onDelete(childId);
    }
  };

  // Determine displayed athletes based on showAll state
  const displayedAthletes = showAll ? athletes : athletes.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMore = athletes.length > INITIAL_DISPLAY_COUNT;
  const remainingCount = athletes.length - INITIAL_DISPLAY_COUNT;

  if (athletes.length === 0 && hasActiveFilters) {
    return (
      <div className="text-center py-16 bg-zinc-900/30 rounded-2xl border border-zinc-800">
        <Search className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">No athletes found</h3>
        <p className="text-zinc-500 mb-4">Try adjusting your search or filters</p>
        <Button
          onClick={onClearFilters}
          variant="outline"
          className="border-[#02b5ff]/50 text-[#02b5ff] hover:bg-[#02b5ff]/10 rounded-xl"
        >
          Clear all filters
        </Button>
      </div>
    );
  }

  if (athletes.length === 0) {
    return (
      <div className="text-center py-20" data-testid="empty-state">
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-3xl p-12 max-w-md mx-auto shadow-2xl border border-zinc-800">
          <div className="w-20 h-20 bg-gradient-to-br from-[#02b5ff]/20 to-[#ff025f]/20 rounded-full flex items-center justify-center mx-auto mb-6 ring-4 ring-[#02b5ff]/10">
            <User className="h-10 w-10 text-[#02b5ff]" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-3">No Athletes Yet</h3>
          <p className="text-zinc-400 mb-8 leading-relaxed">Start tracking growth and performance by adding your first young athlete</p>
          <Button
            data-testid="empty-add-child-button"
            onClick={() => navigate('/add-child')}
            className="h-12 px-8 bg-gradient-to-r from-[#02b5ff] to-[#ff025f] hover:from-[#ff025f] hover:to-[#02b5ff] text-white font-semibold rounded-full shadow-lg shadow-[#02b5ff]/30 transition-all"
          >
            <Plus className="mr-2 h-5 w-5" />
            Add First Child
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="children-grid">
        {displayedAthletes.map((child) => (
          <AthleteCard 
            key={child.id}
            child={child}
            latestMeasurement={childrenData[child.id]}
            medicalStatus={medicalStatuses?.[child.id]}
            onDelete={handleDelete}
            onUpdate={onRefresh}
          />
        ))}
      </div>
      
      {/* See More / See Less Button */}
      {hasMore && (
        <div className="flex justify-center pt-4">
          <Button
            onClick={() => setShowAll(!showAll)}
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-full px-8"
            data-testid="see-more-athletes-btn"
          >
            {showAll ? (
              <>
                <ChevronUp className="h-4 w-4 mr-2" />
                Show Less
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-2" />
                See More ({remainingCount} more athletes)
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
